<template>
	<v-col cols="5" class="mx-auto flex-auto">
		<div class="inventory-form">
			<div class="form-input">
				<label>Size Chart</label>
				<v-select
					v-model="itemData.sizeChart.name"
					:items="sizeCharts ?? []"
					item-text="name"
					item-value="name"
					dense
					outlined
					hide-details="auto"
					@change="renderSizeChartData()"
					:disabled="!canManageInventory"
				></v-select>
				<a
					v-if="itemData.sizeChart.name && canManageInventory"
					class="form-link mt-1"
					color="primary"
					@click.prevent="clearChart()"
					>Clear Chart</a
				>
			</div>

			<div class="form-input">
				<label>Description</label>
				<v-text-field
					outlined
					dense
					hide-details="auto"
					:value="itemData.sizeChart.description"
					disabled
				></v-text-field>
			</div>

			<div class="form-radio">
				<label>Include Length Sizes</label>
				<v-radio-group v-model="itemData.sizeChart.includeLengths" row hide-details="auto">
					<v-radio :disabled="!canManageInventory" label="Yes" :value="true"></v-radio>
					<v-radio :disabled="!canManageInventory" label="No" :value="false"></v-radio>
				</v-radio-group>
			</div>

			<div class="inventory-form-chart">
				<div class="inventory-form-chart-description">
					<p>Place the standard length first in the length field</p>
					<v-btn
						depressed
						outlined
						:disabled="!itemData?.sizeChart?.name"
						@click="showPreview = !showPreview"
					>
						Preview Chart
					</v-btn>
				</div>
				<div
					class="inventory-form-chart-table"
					:class="itemData.sizeChart.includeLengths ? null : 'inventory-form-chart-table-2'"
				>
					<div class="flex-table" role="table-head">
						<div class="flex-table-row" role="row-group">
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
							<div class="flex-table-cell" role="column-header">Size</div>
							<div
								v-if="itemData.sizeChart.includeLengths"
								class="flex-table-cell"
								role="column-header"
							>
								Length
							</div>
							<div class="flex-table-cell" role="column-header">&nbsp;</div>
						</div>
					</div>
					<draggable
						v-model="visibleSizes"
						tag="div"
						class="flex-table"
						role="table-body"
						handle=".drag-handle"
						:disabled="!canManageInventory"
					>
						<div
							v-for="(option, i) in visibleSizes"
							:key="option.sizeId || option.name || i"
							class="flex-table-row"
							:class="option.showSize == false ? 'row-disabled' : null"
							role="row-group"
						>
							<div class="flex-table-cell" role="cell">
								<v-icon
									v-if="canManageInventory"
									class="drag-handle"
									style="cursor: move; font-size: 2rem"
									>mdi-drag</v-icon
								>
								<span v-else>{{ i + 1 }}</span>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="option.name"
										:disabled="option.showSize == false || !canManageInventory"
									></v-text-field>
								</div>
							</div>
							<div v-if="itemData.sizeChart.includeLengths" class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="option.lengths"
										:disabled="option.showSize == false || !canManageInventory"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-btn
									:disabled="!canManageInventory"
									v-show="option.showSize == true"
									icon
									@click="option.showSize = false"
								>
									<v-icon style="font-size: 2rem">mdi-eye</v-icon>
								</v-btn>
								<v-btn
									:disabled="!canManageInventory"
									v-show="option.showSize == false"
									icon
									@click="option.showSize = true"
								>
									<v-icon style="font-size: 2rem">mdi-eye-off</v-icon>
								</v-btn>
							</div>
						</div>
					</draggable>
				</div>
			</div>
		</div>

		<SizingChart
			v-if="showPreview"
			:showSizingChart="showPreview"
			:modal="false"
			:sizes="itemData.sizeChart.sizes"
			:includeLengths="itemData.sizeChart.includeLengths"
			@cancel="showPreview = false"
		/>
	</v-col>
</template>

<script>
import gql from "graphql-tag";
import SizingChart from "@/components/SizingChart.vue";
import draggable from "vuedraggable";
export default {
	name: "InventoryItemSizingView",
	components: {
		SizingChart,
		draggable,
	},
	props: {
		canManageInventory: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			itemData: {
				sizeChart: {
					name: "",
					description: "",
					includeLengths: false,
					sizes: [],
				},
			},
			sizeCharts: [],
			showPreview: false,
		};
	},
	apollo: {
		sizeCharts: {
			query() {
				return gql`
					query {
						sizeCharts(take: 200, order: { name: ASC }, where: { isTemplate: { eq: true } }) {
							items {
								sizeChartId
								name
								description
								isTemplate
								includeLengths
								sizes {
									name
									lengths {
										length
										sizeId
										sizeLengthId
										isRetired
									}
									showSize
									sizeId
									isRetired
									sequence
								}
							}
						}
					}
				`;
			},
			update: (data) => data.sizeCharts.items,
		},
	},
	methods: {
		renderSizeChartData() {
			let sizeChartByName;
			if (this.itemData.sizeChart?.name) {
				sizeChartByName = this.sizeCharts.find((obj) => obj.name == this.itemData.sizeChart?.name);

				//update any itemData related to the selected sizeChart
				this.itemData.sizeChart.templateSizeChartId = sizeChartByName.sizeChartId;
				this.itemData.sizeChart.includeLengths = sizeChartByName.includeLengths;
				this.itemData.sizeChart.description = sizeChartByName.description;
				this.itemData.sizeChart.sizes = sizeChartByName.sizes
					.map((obj) => {
						let sizeObj = {
							sizeId:
								sizeChartByName.includeLengths && obj.lengths.length > 0
									? obj.lengths[0].sizeId
									: obj.sizeId,
							name: obj.name,
							showSize: obj.showSize,
							lengths: this.renderLengthString(obj.lengths),
							isRetired: obj.isRetired,
							sequence: obj.sequence,
						};
						return sizeObj;
					})
					.sort((a, b) => (a.sequence ?? 1000) - (b.sequence ?? 1000));
			}
		},

		renderLengthString(lengthsArr) {
			let activeLengths = lengthsArr.filter((l) => !l.isRetired);
			let lengthString = "";
			//loop through length array and get each size and compile into one string
			activeLengths.forEach((len, i) => {
				if (i !== activeLengths.length - 1) {
					lengthString += `${len.length}, `;
				} else {
					lengthString += `${len.length}`;
				}
			});

			return lengthString;
		},

		clearChart() {
			this.itemData.sizeChart = {
				name: "",
				description: "",
				includeLengths: false,
				sizes: [],
			};
		},
	},
	computed: {
		storeItem() {
			return this.$store.getters.inventoryItem;
		},
		visibleSizes: {
			get() {
				return this.itemData.sizeChart.sizes
					.filter((s) => s.isRetired === false)
					.sort((a, b) => (a.sequence ?? 1000) - (b.sequence ?? 1000));
			},
			set(reordered) {
				// Drag rewrites each visible size's sequence to its new 1-based position.
				// Retired sizes are excluded from display and keep their own sequence.
				reordered.forEach((size, i) => this.$set(size, "sequence", i + 1));
			},
		},
	},
	watch: {
		// Child tabs mount BEFORE the parent view's async mounted() finishes its
		// fetch, and the parent then REPLACES the store object wholesale
		// (newInventoryItem). A one-shot read in mounted() can therefore capture
		// a stale tree from a previous visit (or {} on a hard load) — edits then
		// mutate an orphaned object and never reach the save payload.
		storeItem: {
			immediate: true,
			handler(item) {
				this.itemData = item;

				// If size chart is missing, create it
				// https://github.com/sparkfish/tl-oes/issues/58
				if (this.itemData.sizeChart === undefined || this.itemData.sizeChart === null) {
					this.itemData.sizeChart = {
						name: "",
						description: "",
						includeLengths: false,
						sizes: [],
					};
				}
			},
		},
	},
	beforeDestroy() {
		//push current item data to state
		this.$store.dispatch("updateInventoryItem", this.itemData);

		//let parent component know data has been updated in state
		this.$emit("updated");
	},
};
</script>

<style lang="scss" scoped>
.inventory {
	&-form {
		position: relative;
		display: flex;
		flex-flow: column;
		height: 100%;

		& > .form-radio,
		& > .form-input {
			margin: initial;
			margin-bottom: 1.25rem;
		}

		&-chart {
			display: flex;
			flex-flow: column nowrap;
			height: 100%;
			margin-top: 2rem;

			&-description {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 1.5rem;

				p {
					color: $color-gray;
					font-size: 1.4rem;
				}

				.v-btn {
					background-color: $color-white;
					border-color: $color-gray;
				}
			}

			&-table {
				flex-grow: 1;
				background-color: $color-white;

				.row-disabled {
					opacity: 0.5;
				}

				.flex-table-cell {
					.form-input {
						width: 100%;
					}

					&:nth-child(1) {
						width: 8%;
						justify-content: center;
					}
					&:nth-child(2) {
						width: 42%;
					}
					&:nth-child(3) {
						width: 42%;
					}
					&:nth-child(4) {
						width: 8%;
						justify-content: center;
					}
				}

				&-2 {
					.flex-table-cell {
						.form-input {
							width: 100%;
						}

						&:nth-child(1) {
							width: 8%;
							justify-content: center;
						}
						&:nth-child(2) {
							width: 84%;
						}
						&:nth-child(3) {
							width: 8%;
							justify-content: center;
						}
					}
				}
			}
		}
	}
}
</style>
